import {
  Box,
  HStack,
  Link as ChakraLink,
  Spinner,
  Stack,
  Text,
  useToast,
  useMediaQuery,
  VStack,
  Divider,
} from "@chakra-ui/react";
import { Button } from "./components/Button";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import actions from "src/actions";
import type {
  DisplayMarketingAd,
  DisplayMarketingCampaign,
} from "@common/interfaces/KYDMarketing";
import { PublisherPlatforms } from "@common/interfaces/KYDMarketing";
import type {
  FacebookPage,
  InstagramAccount,
  Step,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import TabSelector from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/TabSelector";
import { useManagedCampaigns } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/hooks/useManagedCampaigns";
import CampaignDetailsCard from "./CampaignDetailsCard";
import CampaignResultsCard from "./CampaignResultsCard";
import CampaignMetricsCard from "./CampaignMetricsCard";
import RightPanel, { type RightPanelWizardStep } from "./RightPanel";
import { StatusBadge } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/StatusBadge";

export default function CampaignDetail() {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const [campaign, setCampaign] = useState<DisplayMarketingCampaign | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [instagramAccounts, setInstagramAccounts] = useState<
    InstagramAccount[]
  >([]);
  const [wizardStep, setWizardStep] = useState<RightPanelWizardStep>("none");
  const [editingAdsetId, setEditingAdsetId] = useState<string | null>(null);
  const [editingAdId, setEditingAdId] = useState<string | null>(null);
  const [wizardReady, setWizardReady] = useState(false);
  const [initialWizardStep, setInitialWizardStep] = useState<Step | null>(null);
  const [generatingIntents, setGeneratingIntents] = useState(false);
  const [isPausingCampaign, setIsPausingCampaign] = useState(false);
  const [isPublishingCampaign, setIsPublishingCampaign] = useState(false);
  const [pausingAdsetId, setPausingAdsetId] = useState<string | null>(null);
  const [publishingAdsetId, setPublishingAdsetId] = useState<string | null>(
    null
  );
  const [activatingAdKey, setActivatingAdKey] = useState<string | null>(null);
  const [pausingAdKey, setPausingAdKey] = useState<string | null>(null);
  const toast = useToast();
  /** When true, layout is column and we show tabs at top to switch Overview vs Insights (same idea as BuildCampaign mobile tabs). */
  const isNarrow = useMediaQuery("(max-width: 991px)")[0];
  const [mobileTab, setMobileTab] = useState<"overview" | "insights">(
    "overview"
  );
  const fetchCampaign = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!campaign_id) return;
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await actions.ADMIN.fetchCampaign(campaign_id, {
          include_event: true,
          // include_llm_intents: true,
        });
        const c =
          (data as { campaign?: DisplayMarketingCampaign })?.campaign ?? data;
        setCampaign(c as DisplayMarketingCampaign);
      } catch (err: any) {
        if (!silent) {
          setError(err?.message ?? "Failed to load campaign");
          toast({
            title: "Error loading campaign",
            description: err?.message,
            status: "error",
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [campaign_id, toast]
  );

  /** Refetch campaign without showing full-page loading (e.g. after intent apply/ignore). */
  const handleRefetchSilent = useCallback(
    () => fetchCampaign({ silent: true }),
    [fetchCampaign]
  );

  const { pollForResult } = useManagedCampaigns({
    defaultCampaign: null,
    defaultEditingAdset: null,
    pages,
    instagramAccounts,
    onPollingComplete: handleRefetchSilent,
  });

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  useEffect(() => {
    if (!campaign?.account_id) return;
    actions.ADMIN.listPages({ ad_account_id: campaign.account_id })
      .then((r: unknown) => setPages((r as FacebookPage[]) ?? []))
      .catch(() => setPages([]));
    actions.ADMIN.listInstagramAccounts({
      ad_account_id: campaign.account_id,
    })
      .then((r: unknown) =>
        setInstagramAccounts((r as InstagramAccount[]) ?? [])
      )
      .catch(() => setInstagramAccounts([]));
  }, [campaign?.account_id]);

  const handlePauseCampaign = useCallback(async () => {
    if (!campaign?.id) return;
    setIsPausingCampaign(true);
    try {
      await actions.ADMIN.unpublishCampaign({ campaign_id: campaign.id });
      toast({ title: "Campaign paused", status: "success" });
      await fetchCampaign({ silent: true });
    } catch (err: any) {
      toast({
        title: "Failed to pause campaign",
        description: err?.message,
        status: "error",
      });
    } finally {
      setIsPausingCampaign(false);
    }
  }, [campaign?.id, fetchCampaign, toast]);

  const handleUnpublishCampaign = useCallback(
    async (payload: { id: string }) => {
      try {
        await actions.ADMIN.unpublishCampaign({ campaign_id: payload.id });
        toast({ title: "Campaign paused", status: "success" });
        await fetchCampaign({ silent: true });
      } catch (err: any) {
        toast({
          title: "Failed to pause campaign",
          description: err?.message,
          status: "error",
        });
      }
    },
    [fetchCampaign, toast]
  );

  const handlePublishAdset = useCallback(
    async (payload: { campaign_id: string; adset_id: string }) => {
      setPublishingAdsetId(payload.adset_id);
      try {
        const response = (await actions.ADMIN.publishAdset(payload)) as {
          update_id?: string;
        };
        if (response?.update_id) {
          await pollForResult({
            campaign_id: payload.campaign_id,
            update_id: response.update_id,
            title: "Ad set activated",
            description: "Ad set activated",
          });
        } else {
          toast({ title: "Ad set activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate ad set",
          description: err?.message,
          status: "error",
        });
      } finally {
        setPublishingAdsetId(null);
      }
    },
    [fetchCampaign, pollForResult, toast]
  );

  const handleUnpublishAdset = useCallback(
    async (payload: { campaign_id: string; adset_id: string }) => {
      setPausingAdsetId(payload.adset_id);
      try {
        await actions.ADMIN.unpublishAdset(payload);
        toast({ title: "Ad set paused", status: "success" });
        await fetchCampaign({ silent: true });
      } catch (err: any) {
        toast({
          title: "Failed to pause ad set",
          description: err?.message,
          status: "error",
        });
      } finally {
        setPausingAdsetId(null);
      }
    },
    [fetchCampaign, toast]
  );

  const handleUnpublishAd = useCallback(
    async (payload: {
      campaign_id: string;
      adset_id: string;
      ad_id: string;
    }) => {
      const key = `${payload.adset_id}:${payload.ad_id}`;
      setPausingAdKey(key);
      try {
        await actions.ADMIN.unpublishAd(payload);
        toast({ title: "Ad paused", status: "success" });
        await fetchCampaign({ silent: true });
      } catch (err: any) {
        toast({
          title: "Failed to pause ad",
          description: err?.message,
          status: "error",
        });
      } finally {
        setPausingAdKey(null);
      }
    },
    [fetchCampaign, toast]
  );

  const handlePublishAd = useCallback(
    async (payload: {
      campaign_id: string;
      adset_id: string;
      ad_id: string;
    }) => {
      const key = `${payload.adset_id}:${payload.ad_id}`;
      setActivatingAdKey(key);
      try {
        const response = (await actions.ADMIN.publishAd(payload)) as {
          update_id?: string;
        };
        if (response?.update_id) {
          await pollForResult({
            campaign_id: payload.campaign_id,
            update_id: response.update_id,
            title: "Ad activated",
            description: "Ad activated",
          });
        } else {
          toast({ title: "Ad activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate ad",
          description: err?.message,
          status: "error",
        });
      } finally {
        setActivatingAdKey(null);
      }
    },
    [fetchCampaign, pollForResult, toast]
  );

  const handlePublishCampaign = useCallback(
    async (payload: { id: string }) => {
      setIsPublishingCampaign(true);
      try {
        const response = (await actions.ADMIN.publishCampaign({
          campaign_id: payload.id,
          publish_all_adsets: true,
        })) as { update_id?: string };
        if (response?.update_id) {
          await pollForResult({
            campaign_id: payload.id,
            update_id: response.update_id,
            title: "Campaign activated",
            description: "Campaign activated",
          });
        } else {
          toast({ title: "Campaign activated", status: "success" });
          await fetchCampaign({ silent: true });
        }
      } catch (err: any) {
        toast({
          title: "Failed to activate campaign",
          description: err?.message,
          status: "error",
        });
      } finally {
        setIsPublishingCampaign(false);
      }
    },
    [fetchCampaign, pollForResult, toast]
  );

  const openEditCampaign = useCallback(() => {
    setEditingAdsetId(null);
    setWizardReady(false);
    setWizardStep("edit_campaign");
  }, []);

  const openEditAdset = useCallback((adsetId: string) => {
    setEditingAdsetId(adsetId);
    setEditingAdId(null);
    setWizardReady(false);
    setInitialWizardStep(null);
    setWizardStep("edit_adset");
  }, []);

  const openEditAd = useCallback(
    (ad: DisplayMarketingAd) => {
      if (!campaign?.adsets || !ad?.id) return;
      const adset = campaign.adsets.find((a) =>
        a.ads?.some((d) => d.id === ad.id)
      );
      if (!adset?.id) return;
      setEditingAdsetId(adset.id);
      setEditingAdId(ad.id);
      setWizardReady(false);
      setInitialWizardStep("edit_creative");
      setWizardStep("edit_adset");
    },
    [campaign?.adsets]
  );

  const openWizardReview = useCallback((adsetId: string) => {
    setEditingAdsetId(adsetId);
    setEditingAdId(null);
    setWizardReady(false);
    setInitialWizardStep("review");
    setWizardStep("edit_adset");
  }, []);

  const closeWizard = useCallback(() => {
    setWizardStep("none");
    setEditingAdsetId(null);
    setEditingAdId(null);
    setWizardReady(false);
    setInitialWizardStep(null);
  }, []);

  const handleWizardReady = useCallback(() => setWizardReady(true), []);

  const handleGenerateIntents = useCallback(async () => {
    if (!campaign?.id) return;
    setGeneratingIntents(true);
    try {
      await actions.ADMIN.runCampaignIntents(campaign.id);
      toast({
        title: "Intents generation started",
        description:
          "It should take ~1:30 minutes to complete -- refresh in a minute to see new recommendations.",
        status: "success",
      });
      await fetchCampaign();
    } catch (err: any) {
      toast({
        title: "Failed to start intents",
        description: err?.message,
        status: "error",
      });
    } finally {
      setGeneratingIntents(false);
    }
  }, [campaign?.id, fetchCampaign, toast]);

  if (loading) {
    return (
      <VStack py={8}>
        <Spinner />
        <Text color="gray.500">Loading campaign...</Text>
      </VStack>
    );
  }

  if (error || !campaign) {
    return (
      <VStack py={8}>
        <Text color="red.500">{error ?? "Campaign not found"}</Text>
        {/* @ts-ignore */}
        <Button as={RouterLink} to="/campaigns/ads" variant="link">
          ← Back to Meta Campaigns
        </Button>
      </VStack>
    );
  }

  return (
    <VStack w="full" h="full" minH={0} flex={1} gap={0} align="stretch">
      <HStack
        w="full"
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={2}
        p={4}
        borderBottom="1px solid #E6E6E6"
      >
        <HStack
          w="full"
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={2}
        >
          <VStack align="flex-start" gap={1}>
            <ChakraLink
              as={RouterLink}
              to="/campaigns/ads"
              fontFamily="Work Sans"
              fontWeight={500}
              fontSize="14px"
              color="#888888"
              letterSpacing="-0.03em"
              verticalAlign="middle"
              _hover={{ textDecoration: "underline" }}
            >
              ← Meta Campaigns
            </ChakraLink>
            <HStack gap={2} justify="center">
              <ChakraLink
                as="span"
                fontWeight="500"
                fontSize="24px"
                lineHeight="30px"
                letterSpacing="-0.03em"
                verticalAlign="middle"
                noOfLines={2}
                _hover={{ opacity: 0.85 }}
                sx={{ leadingTrim: "none" }}
              >
                {campaign.name}
              </ChakraLink>
              <Box>
                <StatusBadge status={campaign.status} />
              </Box>
            </HStack>
          </VStack>
          <HStack gap={2}>
            {campaign.status === "ACTIVE" ? (
              <Button
                boxShadow="0px 4px 10px 0px #0000001A"
                onClick={handlePauseCampaign}
                isLoading={isPausingCampaign}
                isDisabled={isPausingCampaign}
              >
                Pause Campaign
              </Button>
            ) : campaign.status === "PAUSED" ? (
              <Button
                boxShadow="0px 4px 10px 0px #0000001A"
                onClick={() => handlePublishCampaign({ id: campaign.id })}
                isLoading={isPublishingCampaign}
                isDisabled={isPublishingCampaign}
              >
                Activate Campaign
              </Button>
            ) : null}
          </HStack>
        </HStack>
      </HStack>

      {/* On narrow viewports: tabs at top (same style as AdPreview TabSelector) */}
      {isNarrow && (
        <Box
          w="full"
          p={4}
          borderBottom="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          <TabSelector
            size="sm"
            optionId={mobileTab}
            onOptionChange={(optionId) =>
              setMobileTab(optionId as "overview" | "insights")
            }
            options={[
              { id: "overview", label: "Overview", content: null },
              { id: "insights", label: "Insights & Ad sets", content: null },
            ]}
            spacing={2}
          />
        </Box>
      )}

      <Stack
        w="full"
        flex={1}
        minH={0}
        spacing={0}
        direction={{ base: "column", lg: "row" }}
        align="stretch"
      >
        {/* Left bar: campaign overview — matches Meta Campaigns detail layout. On narrow, only show when Overview tab is selected. */}
        {(!isNarrow || mobileTab === "overview") && (
          <Box
            w={{ base: "100%", lg: "auto" }}
            minW={{ base: "100%", lg: "375px", xl: "475px" }}
            maxW={{ base: "100%", lg: "40%" }}
            minH={0}
            overflowY="auto"
            bg="white"
            p={4}
          >
            <VStack align="stretch" spacing={5}>
              <CampaignDetailsCard campaign={campaign} />
              <Divider borderColor="gray.200" />
              <CampaignResultsCard campaign={campaign} />
              <Divider borderColor="gray.200" />
              <CampaignMetricsCard campaign={campaign} />
            </VStack>
          </Box>
        )}

        {/* Right: insights + ad sets, or edit wizard. On narrow, only show when Insights tab is selected. */}
        {(!isNarrow || mobileTab === "insights") && (
          <RightPanel
            campaign={campaign}
            pages={pages}
            instagramAccounts={instagramAccounts}
            wizardStep={wizardStep}
            editingAdsetId={editingAdsetId}
            editingAdId={editingAdId}
            wizardReady={wizardReady}
            onEditCampaign={openEditCampaign}
            onEditAdset={openEditAdset}
            onEditAd={openEditAd}
            onWizardReady={handleWizardReady}
            onCloseWizard={closeWizard}
            onRefetch={fetchCampaign}
            onRefetchSilent={handleRefetchSilent}
            onPauseCampaign={handlePauseCampaign}
            onPublishCampaign={handlePublishCampaign}
            onUnpublishCampaign={handleUnpublishCampaign}
            onPublishAdset={handlePublishAdset}
            onPublishAd={handlePublishAd}
            onUnpublishAdset={handleUnpublishAdset}
            onUnpublishAd={handleUnpublishAd}
            isPublishingCampaign={isPublishingCampaign}
            activatingAdKey={activatingAdKey}
            pausingAdsetId={pausingAdsetId}
            publishingAdsetId={publishingAdsetId}
            pausingAdKey={pausingAdKey}
            onOpenWizardReview={openWizardReview}
            initialWizardStep={initialWizardStep}
            onInitialStepConsumed={() => setInitialWizardStep(null)}
          />
        )}
      </Stack>
    </VStack>
  );
}
